import {
  Content as RadixContent,
  Portal as RadixPortal,
} from '@radix-ui/react-dropdown-menu';
import {
  forwardRef,
  useCallback,
  useContext,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';

import { useOverlayStacking } from '../../core/hooks/useOverlayStacking.js';
import { MobileMenuModeContext } from './contexts/MobileMenuModeContext.js';
import { useMenuContext } from './hooks/useMenuContext.js';
import * as styles from './Menu.sty.js';
import { MobileContent } from './MobileContent.js';
import { type MenuContentProps } from './types.js';

export type { MenuContentProps };

const SIDE_OFFSET = 4;
type InternalMenuContentProps = MenuContentProps & {
  /** @internal */
  _onTabKeyPressed: () => void;
  /** @internal */
  _sideOffset?: number;
  /** @internal */
  _alignOffset?: number;
};

/**
 * The component that pops out when the dropdown menu is open.
 * @public
 */
export const Content = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  MenuContentProps
>((props, forwardedRef) => {
  const isMobile = useContext(MobileMenuModeContext);

  const {
    side = 'bottom',
    alignment = 'start',
    onCloseAutoFocus,
    onInteractOutside,
    avoidCollisions = true,
    _sideOffset = SIDE_OFFSET,
    _alignOffset,
    _onTabKeyPressed,
    children,
    collisionBoundary,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  } = props as InternalMenuContentProps;
  const tabKeyHandler = useCallback(
    (e: ReactKeyboardEvent) => {
      if (_onTabKeyPressed && e.key === 'Tab') {
        _onTabKeyPressed();
      }
    },
    [_onTabKeyPressed],
  );
  const { style } = useOverlayStacking(undefined, 'overlay');
  const { setIsOpen, contentId } = useMenuContext();

  const handleOnKeyDown = useCallback(
    (e: ReactKeyboardEvent) => {
      // Only close menu on escape when it is nested in other overlays (modal, sheet, ...).
      if (e.key === 'Escape') {
        setIsOpen(false);
        e.stopPropagation();
      }
    },
    [setIsOpen],
  );

  if (isMobile) {
    return <MobileContent {...props} _contentId={contentId} />;
  }

  return (
    <RadixPortal>
      <RadixContent
        {...remainingProps}
        id={contentId}
        data-testid={dataTestId}
        ref={forwardedRef}
        side={side}
        align={alignment}
        onKeyUp={tabKeyHandler}
        onCloseAutoFocus={onCloseAutoFocus}
        onInteractOutside={onInteractOutside}
        sideOffset={_sideOffset}
        alignOffset={_alignOffset}
        avoidCollisions={avoidCollisions}
        className={`${styles.content} ${styles.groupContainer}`}
        style={{ zIndex: style.zIndex }}
        onKeyDown={handleOnKeyDown}
        collisionBoundary={collisionBoundary}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
      >
        {children}
      </RadixContent>
    </RadixPortal>
  );
});

(Content as typeof Content & { displayName: string }).displayName =
  'Menu.Content';
